import { useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import { AccountType } from '../../../services/utils/types';
import { postShare } from '../../../services/LinkedIn';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../ACSnackbar';
import LinkedInLogo from '../ShareItems/linked_In_logo.svg';

const useStyles = () => ({
  textHeadline: {
    fontWeight: '600',
    fontSize: '20px',
    padding: '8px 10px',
    color: '#4D586F',
  },
  textContent: {
    fontWeight: '400',
    fontSize: '14px',
    padding: '8px 10px',
    color: '#001030',
    padding: '0px',
    mb: '24px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function LinkedinModalContent(props) {
  const { handleClose, isLoading, setIsLoading, forceSnackbar } = props;
  const classes = useStyles();
  const { accountDetails, type } = useAccount();
  const isCompany = type === AccountType.COMPANY;
  const { displayName } = accountDetails;
  const [linkedInPostTitle, setLinkedInPostTitle] = useState(
    isCompany
      ? `Join the ${displayName}'s Advisory Board!`
      : 'My availability as an advisor!'
  );
  const [postBody, setBodyTitle] = useState(
    isCompany
      ? `I've built the ${displayName} Advisory Board on @advisorycloud, and would love for you to join as an advisor.`
      : `I'm now available as an advisor on @advisorycloud. Connect with me on a variety of topics and high-level networking here: ${window.location.origin}/profile/${accountDetails.publicProfileUrl}`
  );
  const [successMessage, setSuccessMessage] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [gotLinkedinResponse, setGotLinkedinResponse] = useState(false);

  async function linkedInResponse(linkedinResponse) {
    if (linkedinResponse === 201) {
      setSuccessMessage('Your profile is shared successfully.');
      setSuccessSnackbar(true);
      setGotLinkedinResponse(true);
    } else if (linkedinResponse === 422) {
      setSuccessMessage(
        'You have already posted this content, either update the post or try again in 10 minutes'
      );
      setSuccessSnackbar(true);
      setGotLinkedinResponse(true);
    } else {
      setSuccessMessage('An error occurred while sharing your profile.');
      setSuccessSnackbar(true);
    }
  }

  async function postInLinkedIn() {
    setIsLoading(true);
    try {
      const link = isCompany
        ? `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`
        : `https://app.advisorycloud.com/profile/${accountDetails.publicProfileUrl}`;
      const postResponse = await postShare(
        linkedInPostTitle,
        postBody,
        link,
        'https://app.advisorycloud.com/images/linkedin-thumbnail.png',
        isCompany ? accountDetails.id : null
      );
      linkedInResponse(postResponse);
    } catch (error) {
      console.error('Error posting to LinkedIn:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        sx={{ ...classes.textHeadline, padding: '12px 12px' }}
      >
        <img
          src={LinkedInLogo}
          alt="LinkedIn Logo"
          style={{ marginRight: '12px' }}
        />
        Share on LinkedIn
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        <Typography sx={classes.textContent}>
          {isCompany
            ? "We've made it easy to share your advisory board with your followers on LinkedIn, so your most engaged followers can join! Simply click below to post a message to your LinkedIn audience."
            : "We've made it easy to share your AdvisoryCloud Profile with your existing network on LinkedIn, so colleagues and contacts can connect with you as an advisor! Simply click below to post a message to your LinkedIn audience."}
        </Typography>
        <TextField
          id="advisorHeadline"
          placeholder="Title"
          variant="outlined"
          fullWidth
          value={linkedInPostTitle}
          onChange={(evt) => setLinkedInPostTitle(evt.target.value)}
          inputProps={{
            maxLength: 200,
          }}
          sx={{
            marginBottom: '24px',
          }}
          InputProps={{
            style: {
              borderRadius: '8px',
            },
          }}
        />
        <TextField
          placeholder="Description"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={postBody}
          onChange={(evt) => setBodyTitle(evt.target.value)}
          InputProps={{
            style: {
              borderRadius: '8px',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            postInLinkedIn();
          }}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
          disabled={
            !postBody || !linkedInPostTitle || isLoading || gotLinkedinResponse
          }
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Share on LinkedIn'}
        </Button>
      </DialogActions>
      <ACSnackbar
        open={successSnackbar}
        text={successMessage}
        severity="success"
        onClose={() => {
          setSuccessSnackbar(false);
          if (gotLinkedinResponse) {
            handleClose();
          }
        }}
        autoHideDuration={6000}
        sx={
          forceSnackbar
            ? {
                transform: 'translateX(-0%) !important',
              }
            : {}
        }
      />
    </>
  );
}
